import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldStar, Coins, Users, Receipt, MapTrifold, ClipboardText, Sliders, FirstAid, Lock } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Admin() {
  const { user, refresh } = useAuth();

  if (user?.password_change_required) {
    return <ChangePasswordGate onDone={refresh} />;
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldStar size={26} weight="duotone" className="text-ff-gold" />
          <h1 className="font-display text-4xl font-bold tracking-tight">Admin Console</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/audio" data-testid="admin-audio-link" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs uppercase tracking-wider text-ff-brass transition-colors hover:border-ff-gold hover:text-ff-gold">
            Expedition Pulse
          </Link>
          <Link to="/admin/commissioned-expeditions" data-testid="admin-ce-link" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs uppercase tracking-wider text-ff-brass transition-colors hover:border-ff-gold hover:text-ff-gold">
            Commissioned Expeditions
          </Link>
        </div>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-4 flex-wrap bg-ff-bg2">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={16} className="mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="crew" data-testid="admin-tab-crew"><Users size={16} className="mr-1" /> Player Crew</TabsTrigger>
          <TabsTrigger value="expeditions" data-testid="admin-tab-expeditions"><MapTrifold size={16} className="mr-1" /> Expeditions</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="admin-tab-transactions"><Receipt size={16} className="mr-1" /> Ledger</TabsTrigger>
          <TabsTrigger value="medical" data-testid="admin-tab-medical"><FirstAid size={16} className="mr-1" /> Medical</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="admin-tab-sessions"><Coins size={16} className="mr-1" /> Sessions</TabsTrigger>
          <TabsTrigger value="config" data-testid="admin-tab-config"><Sliders size={16} className="mr-1" /> Config</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><ClipboardText size={16} className="mr-1" /> Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="crew"><PlayerCrewTab /></TabsContent>
        <TabsContent value="expeditions"><ExpeditionsTab /></TabsContent>
        <TabsContent value="transactions"><TransactionsTab /></TabsContent>
        <TabsContent value="medical"><MedicalTab /></TabsContent>
        <TabsContent value="sessions"><SessionsTab /></TabsContent>
        <TabsContent value="config"><ConfigTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ChangePasswordGate({ onDone }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated. Admin tools unlocked.");
      onDone && onDone();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md" data-testid="admin-password-gate">
      <div className="ff-panel rounded-md p-8">
        <div className="mb-2 flex items-center gap-2 text-ff-gold"><Lock size={22} weight="duotone" /><span className="font-mono-ff text-xs uppercase tracking-widest">Security</span></div>
        <h1 className="font-display text-3xl font-bold">Change Default Password</h1>
        <p className="mt-2 text-sm text-ff-muted">The default admin password must be changed before admin tools can be used.</p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input data-testid="gate-current-password" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} required className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-3 py-2.5 text-sm text-ff-parchment" />
          <input data-testid="gate-new-password" type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} required className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-3 py-2.5 text-sm text-ff-parchment" />
          <button data-testid="gate-submit" disabled={busy} className="w-full rounded-sm bg-ff-gold py-3 font-semibold text-ff-bg hover:bg-ff-brass disabled:opacity-60">
            {busy ? "..." : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Panel({ children }) {
  return <div className="ff-panel overflow-x-auto rounded-md p-4">{children}</div>;
}
function Th({ children }) { return <th className="px-3 py-2 text-left font-medium text-ff-brass">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 text-ff-parchment ${className}`}>{children}</td>; }

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const grant = async (id) => {
    const amount = Number(prompt("Grant how much Gold? (use negative to deduct)", "500"));
    if (!amount || Number.isNaN(amount)) return;
    try {
      await api.post("/admin/grant-gold", { user_id: id, amount });
      toast.success("Gold adjusted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const recalc = async (id) => {
    try {
      const { data } = await api.post(`/admin/rank/${id}/recalculate`);
      toast.success(`Rank: ${data.current.name}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Panel>
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead><tr className="border-b border-ff-brass/20"><Th>Username</Th><Th>Email</Th><Th>Role</Th><Th>Gold</Th><Th>Rep</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-ff-brass/10" data-testid={`admin-user-${u.id}`}>
              <Td>{u.username}</Td>
              <Td className="text-ff-muted">{u.email}</Td>
              <Td><span className={u.role === "admin" ? "text-ff-gold" : "text-ff-muted"}>{u.role}</span></Td>
              <Td className="font-mono-ff text-ff-gold">{u.balance}</Td>
              <Td className="font-mono-ff">{u.profile?.reputation ?? "-"}</Td>
              <Td>
                <div className="flex gap-1.5">
                  <button onClick={() => grant(u.id)} data-testid={`grant-gold-${u.id}`} className="rounded-sm border border-ff-gold/40 px-2 py-1 text-xs text-ff-gold hover:bg-ff-gold/10">Grant Gold</button>
                  <button onClick={() => recalc(u.id)} data-testid={`recalc-rank-${u.id}`} className="rounded-sm border border-ff-brass/40 px-2 py-1 text-xs text-ff-brass hover:bg-ff-brass/10">Recalc Rank</button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function ExpeditionsTab() {
  const [exps, setExps] = useState([]);
  const load = () => api.get("/admin/expeditions").then((r) => setExps(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/expeditions/${id}`, body);
      toast.success("Expedition updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {exps.map((e) => (
        <div key={e.id} className="ff-panel rounded-md p-5" data-testid={`admin-exp-${e.id}`}>
          <h3 className="font-display text-xl font-semibold">{e.name}</h3>
          <p className="text-xs text-ff-muted">{e.difficulty} · factor {e.difficulty_factor}</p>

          <div className="mt-3 space-y-2 text-xs">
            <label className="block">Difficulty factor (0-1)
              <input type="number" step="0.05" min="0" max="1" defaultValue={e.difficulty_factor}
                onBlur={(ev) => patch(e.id, { difficulty_factor: Number(ev.target.value) })}
                data-testid={`exp-difficulty-${e.id}`}
                className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1.5 text-ff-parchment" />
            </label>
            <div className="flex gap-2">
              <label className="flex-1">Min stake
                <input type="number" defaultValue={e.min_stake} onBlur={(ev) => patch(e.id, { min_stake: Number(ev.target.value) })}
                  data-testid={`exp-min-${e.id}`} className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1.5 text-ff-parchment" />
              </label>
              <label className="flex-1">Max stake
                <input type="number" defaultValue={e.max_stake} onBlur={(ev) => patch(e.id, { max_stake: Number(ev.target.value) })}
                  data-testid={`exp-max-${e.id}`} className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1.5 text-ff-parchment" />
              </label>
            </div>
            <label className="block">Reward × (Critical Success)
              <input type="number" step="0.1" defaultValue={e.reward_multipliers?.critical_success}
                onBlur={(ev) => patch(e.id, { reward_multipliers: { ...e.reward_multipliers, critical_success: Number(ev.target.value) } })}
                data-testid={`exp-reward-${e.id}`} className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1.5 text-ff-parchment" />
            </label>
            <div className="flex gap-2">
              <label className="flex-1">Min player rank
                <input type="number" min="1" max="6" defaultValue={e.min_player_rank || ""} placeholder="none"
                  onBlur={(ev) => patch(e.id, { min_player_rank: ev.target.value ? Number(ev.target.value) : 0 })}
                  data-testid={`exp-minrank-${e.id}`} className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1.5 text-ff-parchment" />
              </label>
              <label className="flex-1">Min reputation
                <input type="number" defaultValue={e.min_reputation || ""} placeholder="none"
                  onBlur={(ev) => patch(e.id, { min_reputation: ev.target.value ? Number(ev.target.value) : 0 })}
                  data-testid={`exp-minrep-${e.id}`} className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1.5 text-ff-parchment" />
              </label>
            </div>
          </div>

          <button
            onClick={() => patch(e.id, { enabled: !e.enabled })}
            data-testid={`exp-toggle-${e.id}`}
            className={`mt-4 w-full rounded-sm py-2 text-sm font-medium transition-colors ${e.enabled ? "bg-ff-danger/20 text-ff-danger hover:bg-ff-danger/30" : "bg-ff-success/20 text-ff-success hover:bg-ff-success/30"}`}
          >
            {e.enabled ? "Disable Expedition" : "Enable Expedition"}
          </button>
        </div>
      ))}
    </div>
  );
}

function TransactionsTab() {
  const [tx, setTx] = useState([]);
  useEffect(() => { api.get("/admin/transactions").then((r) => setTx(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  return (
    <Panel>
      <table className="w-full text-sm" data-testid="admin-transactions-table">
        <thead><tr className="border-b border-ff-brass/20"><Th>Type</Th><Th>Amount</Th><Th>Before</Th><Th>After</Th><Th>When</Th></tr></thead>
        <tbody>
          {tx.map((t) => (
            <tr key={t.id} className="border-b border-ff-brass/10">
              <Td>{t.type}</Td>
              <Td className={`font-mono-ff ${t.amount >= 0 ? "text-ff-success" : "text-ff-danger"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</Td>
              <Td className="font-mono-ff text-ff-muted">{t.balance_before}</Td>
              <Td className="font-mono-ff text-ff-gold">{t.balance_after}</Td>
              <Td className="text-ff-muted">{new Date(t.timestamp).toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function SessionsTab() {
  const [ss, setSs] = useState([]);
  useEffect(() => { api.get("/admin/sessions").then((r) => setSs(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  return (
    <Panel>
      <table className="w-full text-sm" data-testid="admin-sessions-table">
        <thead><tr className="border-b border-ff-brass/20"><Th>Expedition</Th><Th>Stake</Th><Th>Status</Th><Th>Outcome</Th><Th>When</Th></tr></thead>
        <tbody>
          {ss.map((s) => (
            <tr key={s.id} className="border-b border-ff-brass/10">
              <Td>{s.expedition_name}</Td>
              <Td className="font-mono-ff">{s.stake}</Td>
              <Td className="text-ff-muted">{s.status}</Td>
              <Td>{s.result?.outcome_label || "-"}</Td>
              <Td className="text-ff-muted">{new Date(s.created_at).toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  return (
    <Panel>
      <table className="w-full text-sm" data-testid="admin-audit-table">
        <thead><tr className="border-b border-ff-brass/20"><Th>Action</Th><Th>Details</Th><Th>When</Th></tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-ff-brass/10">
              <Td className="text-ff-gold">{l.action}</Td>
              <Td className="font-mono-ff text-xs text-ff-muted">{JSON.stringify(l.details)}</Td>
              <Td className="text-ff-muted">{new Date(l.timestamp).toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function PlayerCrewTab() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/users").then((r) => { const pl = r.data.filter((u) => u.role === "user"); setUsers(pl); if (pl[0]) setUid(pl[0].id); }); }, []);
  const load = (id) => api.get(`/admin/player-crew/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (uid) load(uid); }, [uid]);

  const adjust = async (crew_id, patch) => {
    try {
      await api.post("/admin/crew-adjust", { user_id: uid, crew_id, reason: "Admin console adjustment", ...patch });
      toast.success("Crew adjusted");
      load(uid);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <select value={uid} onChange={(e) => setUid(e.target.value)} data-testid="admin-crew-user-select" className="mb-4 rounded-sm border border-ff-brass/30 bg-ff-bg px-3 py-2 text-sm text-ff-parchment">
        {users.map((u) => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
      </select>
      {data && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.crew.map((c) => (
            <div key={c.id} className="ff-panel rounded-md p-4" data-testid={`admin-crew-${c.id}`}>
              <div className="flex items-center gap-2">
                <img src={c.portrait} alt={c.name} className="h-10 w-10 rounded-sm object-cover" />
                <div>
                  <div className="text-sm font-medium text-ff-parchment">{c.name}</div>
                  <div className="text-xs text-ff-muted">Lv {c.level} · XP {c.xp} · {c.available ? "Available" : "Unavailable"}</div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[11px] text-ff-muted">
                <div><div className="font-mono-ff text-ff-parchment">{c.loyalty}</div>Loyalty</div>
                <div><div className="font-mono-ff text-ff-parchment">{c.stress}</div>Stress</div>
                <div><div className={`font-mono-ff ${c.injury_status ? "text-ff-danger" : "text-ff-parchment"}`}>{c.injury_status ? c.injury_severity : c.health}</div>Health</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
                <button onClick={() => adjust(c.id, { stress: Number(prompt("Set stress (0-100)", c.stress)) })} data-testid={`adjust-stress-${c.id}`} className="rounded-sm border border-ff-brass/40 px-2 py-1 text-ff-brass hover:bg-ff-brass/10">Stress</button>
                <button onClick={() => adjust(c.id, { loyalty: Number(prompt("Set loyalty (0-100)", c.loyalty)) })} data-testid={`adjust-loyalty-${c.id}`} className="rounded-sm border border-ff-brass/40 px-2 py-1 text-ff-brass hover:bg-ff-brass/10">Loyalty</button>
                <button onClick={() => adjust(c.id, { clear_injury: true })} data-testid={`clear-injury-${c.id}`} className="rounded-sm border border-ff-success/40 px-2 py-1 text-ff-success hover:bg-ff-success/10">Clear Injury</button>
                <button onClick={() => adjust(c.id, { active: !c.active })} data-testid={`toggle-active-${c.id}`} className="rounded-sm border border-ff-danger/40 px-2 py-1 text-ff-danger hover:bg-ff-danger/10">{c.active ? "Deactivate" : "Activate"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MedicalTab() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/admin/medical-supplies").then((r) => setEvents(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  return (
    <Panel>
      <table className="w-full text-sm" data-testid="admin-medical-table">
        <thead><tr className="border-b border-ff-brass/20"><Th>Amount</Th><Th>Before</Th><Th>After</Th><Th>Reason</Th><Th>When</Th></tr></thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} className="border-b border-ff-brass/10">
              <Td className={`font-mono-ff ${e.amount >= 0 ? "text-ff-success" : "text-ff-danger"}`}>{e.amount >= 0 ? "+" : ""}{e.amount}</Td>
              <Td className="font-mono-ff text-ff-muted">{e.before}</Td>
              <Td className="font-mono-ff text-ff-gold">{e.after}</Td>
              <Td className="text-ff-muted">{e.reason}</Td>
              <Td className="text-ff-muted">{new Date(e.timestamp).toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiErr(e))); }, []);

  const saveGroup = async (key, obj) => {
    try {
      await api.put("/admin/config", { [key]: obj });
      toast.success("Config saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!cfg) return <div className="text-ff-muted">Loading config...</div>;
  const groups = [
    { key: "injury_probabilities", label: "Injury Probabilities (0-1)", step: "0.01" },
    { key: "stress_modifiers", label: "Stress Modifiers", step: "1" },
    { key: "loyalty_modifiers", label: "Loyalty Modifiers", step: "1" },
    { key: "crew_xp", label: "Crew XP per Outcome", step: "1" },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {groups.map((g) => (
        <div key={g.key} className="ff-panel rounded-md p-5" data-testid={`config-${g.key}`}>
          <h3 className="mb-3 font-display text-lg font-semibold">{g.label}</h3>
          <div className="space-y-2">
            {Object.keys(cfg[g.key]).map((outcome) => (
              <label key={outcome} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-ff-muted">{outcome.replace("_", " ")}</span>
                <input type="number" step={g.step} defaultValue={cfg[g.key][outcome]}
                  onBlur={(ev) => { const next = { ...cfg[g.key], [outcome]: Number(ev.target.value) }; setCfg({ ...cfg, [g.key]: next }); saveGroup(g.key, next); }}
                  data-testid={`config-${g.key}-${outcome}`}
                  className="w-24 rounded-sm border border-ff-brass/30 bg-ff-bg px-2 py-1 text-right text-ff-parchment" />
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
